import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Stack,
  Group,
  Text,
  Button,
  Badge,
  Alert,
  Box,
  Grid,
  ActionIcon,
  Drawer,
  Select,
  MultiSelect,
  Switch,
  Loader,
  Center,
  Tooltip,
  Divider,
  Modal,
  TextInput,
  Card,
  Accordion
} from '@mantine/core';
import {
  IconMapPin,
  IconGrid3x3,
  IconRefresh,
  IconDownload,
  IconPlus,
  IconAlertTriangle,
  IconX,
  IconFilter,
  IconCopy,
  IconRepeat,
  IconCheck,
  IconUsers,
  IconBuilding,
  IconChartBar,
  IconClock,
  IconAlertCircle,
  IconInfoCircle,
  IconEdit,
  IconTrash,
  IconCalendar,
  IconSettings,
  IconSelect,
  IconDoor,
  IconStairs,
  IconQuestionMark
} from '@tabler/icons-react';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';

// Import room viewer drawer
import { RoomDetailSidebar } from '../../components/drawers';
import { SessionTemplatesDrawer } from '../../components/drawers/SessionTemplatesDrawer';

// Import our enhanced components
import RoomCentricPlannerGrid from '../../components/organisms/RoomCentricPlannerGrid';
import FloorPlanCanvas from '../../components/organisms/FloorPlanCanvas';
import FloorPlanLegend from '../../components/molecules/FloorPlanLegend';
import { RoomCalendarView } from '../../features/coordinator/planner'; // ADDED: 10-10-2025 21:02:31 - Calendar view integration

// NEW: Import modern 2025 components
import { AnimatedButton } from '../../components/atoms/AnimatedButton';
import { ActiveFiltersChips } from '../../components/molecules/ActiveFiltersChips';
import { ExpandablePanel } from '../../components/molecules/ExpandablePanel';
import { ViewModeToggle } from '../../components/molecules/ViewModeToggle';
import { BulkActionToolbar } from '../../components/molecules/BulkActionToolbar';
import { FilterChip } from '../../types/planner';

// Import date utilities
import { calculateDateRange, type ViewMode, type DateRange } from '../../utils/dateHelpers';
import { format } from 'date-fns';

// Import Floor Plan types for context menu
import type { RoomContextAction } from '../../types/planner';

// Import modal components
import {
  AssignModal,
  BulkAssignModal,
  ExportModal,
  CopyModal,
  RecurrenceModal,
  PublishModal
} from '../../components/modals';
import { StatusLegendModal } from '../../components/modals/StatusLegendModal';

// Import hooks and stores
import {
  usePlannerGridData,
  usePlannerFilters,
  useFloorPlanData,
  useInvalidatePlannerQueries
} from '../../hooks/api/usePlannerData';
import {
  usePlannerStore
} from '../../stores/plannerStore';
import { API_CONFIG } from '../../api';
import { useUndoState } from '../../hooks/useUndoState';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useCoordinatorKPIs } from '../../hooks/coordinator/useCoordinatorDashboard';
import { useHolidays } from '../../hooks/admin/useHolidays';

// Import error boundaries and loading states
import { GridErrorBoundary, FloorPlanErrorBoundary } from '../../components/error/PlannerErrorBoundary';
import toastSystem from '../../components/notifications/EnhancedToastSystem';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

// interface PlannerPageFilters {
//   startDate: Date;
//   endDate: Date;
//   buildingId?: string;
//   floorId?: string;
//   roomIds: string[];
//   sessionTemplateIds: string[];
//   userIds: string[];
//   statuses: string[];
//   includeBlocks: boolean;
//   includeConflicts: boolean;
//   includeUtilization: boolean;
//   onlyVacancies: boolean;
// }

interface SidePanelContent {
  type: 'room-details' | 'conflicts' | 'utilization' | 'assignment-details' | null;
  data?: any;
}

interface EnhancedPlannerPageProps {
  // Props for testing or customization
  initialView?: 'grid' | 'floor-plan';
  initialDate?: Date;
  showSidePanel?: boolean;
}

// =============================================================================
// FILTER STATE MANAGEMENT
// =============================================================================

/**
 * Enhanced Planner Page Component
 * 
 * Features from UX Flow Spec:
 * - Comprehensive toolbar with filters and actions
 * - View toggle between Grid and Floor Plan
 * - Collapsible side panel for details
 * - Real-time synchronization between views
 * - Advanced filtering and export capabilities
 * - Responsive design for all screen sizes
 * - Keyboard navigation and accessibility
 */
const EnhancedPlannerPage: React.FC<EnhancedPlannerPageProps> = ({
  initialView = 'grid',
  initialDate = new Date(),
  showSidePanel = false
}) => {
  // =============================================================================
  // STATE MANAGEMENT
  // =============================================================================
  
  // URL params for deep linking (ADDED: 08-10-2025 18:03:30 - Gap P1)
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get roomId from URL for drawer
  const roomId = searchParams.get('roomId');
  
  // Initialize with clean filter state - no persistence
  
  // UPDATED: 10-10-2025 21:02:31 - Added 'calendar' view option
  const [activeView, setActiveView] = useState<'grid' | 'floor-plan' | 'calendar'>(initialView);
  
  // Date range and view mode (UPDATED: 08-10-2025 - Support week/month views)
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [dateRange, setDateRange] = useState<DateRange>(() => 
    calculateDateRange('week', initialDate)
  );
  
  // Initialize filters from URL params (URL takes precedence over localStorage)
  // (UPDATED: 08-10-2025 18:03:30 - Gap P1 + P3)
    // Clean filter state - always start with no filters active
  const [selectedBuilding, setSelectedBuilding] = useState<string | null>(null);
  const [selectedFloor, setSelectedFloor] = useState<string | null>(null);
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [selectedSessionTimes, setSelectedSessionTimes] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [onlyVacancies, setOnlyVacancies] = useState<boolean>(false);
  
  // UI State
  const [sidePanelOpen, setSidePanelOpen] = useState(showSidePanel);
  const [sidePanelContent, setSidePanelContent] = useState<SidePanelContent>({ type: null });
  const [isFullscreen] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showLegendModal, setShowLegendModal] = useState(false);
  const [showInstructions, setShowInstructions] = useState(() => {
    return localStorage.getItem('instructionsPanelDismissed') !== 'true';
  });
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  
  // Modal States
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showRecurrenceModal, setShowRecurrenceModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  // @ts-expect-error - Added for future BlockModal integration (Floor Plan context menu "Create block" action)
  const [showBlockModal, setShowBlockModal] = useState(false); // ADDED: 09-10-2025 12:12:58
  const [showDeleteRoomModal, setShowDeleteRoomModal] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState<any | undefined>(null);
  const [showTemplatesDrawer, setShowTemplatesDrawer] = useState(false); // NEW: 10-10-2025 19:49:52
  
  // FIXED: 08-10-2025 19:03:31 - Assignment data for modal pre-filling
  const [assignmentData, setAssignmentData] = useState<{
    roomId: string;
    sessionTemplateId: string;
    date: string;
  } | null>(null);
  
  
  // =============================================================================
  // STORE INTEGRATION
  // =============================================================================
  
  const {
    setSelectedFloor: setStoreSelectedFloor,
    setActiveView: setStoreActiveView,
    setSidePanelOpen: setStoreSidePanelOpen,
    setSidePanelContent: setStoreSidePanelContent,
    clearSelectedCells,
    toggleLegendFilter,
    clearLegendFilters
  } = usePlannerStore();
  
  // Get selected cells count from store for multi-select badge
  const selectedCells = usePlannerStore((state) => state.ui.selectedCells);
  
  // ADDED: 09-10-2025 12:12:58 - Get legend filter state for Floor Plan opacity filtering
  const legendFilters = usePlannerStore((state) => state.ui.legendFilters);
  
  // const { hasActive, summary } = useActiveFilters();
  // const { count, bulkMode } = useSelectedAssignments();
  
  // =============================================================================
  // API HOOKS
  // =============================================================================
  
  const { invalidateAll } = useInvalidatePlannerQueries();
  
  // =============================================================================
  // UX ENHANCEMENT HOOKS (Phase 1)
  // =============================================================================
  
  // Undo functionality with 5-second window
  const { registerUndo, executeUndo, canUndo, remainingSeconds } = useUndoState();
  
  // Keyboard shortcuts for power users
  useKeyboardShortcuts({
    onOpenFilters: () => setShowFilters(true),
    onNewAssignment: () => setShowAssignModal(true),
    onExport: () => setShowExportModal(true),
    onPublish: () => setShowPublishModal(true),
    onCloseModals: () => {
      setShowAssignModal(false);
      setShowBulkAssignModal(false);
      setShowExportModal(false);
      setShowCopyModal(false);
      setShowRecurrenceModal(false);
      setShowPublishModal(false);
      setSidePanelOpen(false);
    },
    enabled: true
  });
  
  // Convert dates to string format for API calls (UPDATED: 08-10-2025 - Use date range)
  const apiFilters = useMemo(() => ({
    startDate: format(dateRange[0], 'yyyy-MM-dd'),
    endDate: format(dateRange[1], 'yyyy-MM-dd'),
    ...(selectedBuilding && { buildingId: selectedBuilding }),
    ...(selectedFloor && { floorId: selectedFloor }),
    ...(selectedRooms.length > 0 && { roomIds: selectedRooms }),
    ...(selectedSessionTimes.length > 0 && { sessionTemplateIds: selectedSessionTimes }),
    ...(selectedUsers.length > 0 && { userIds: selectedUsers }),
    ...(selectedStatuses.length > 0 && { statuses: selectedStatuses }),
    includeBlocks: true,
    includeConflicts: true,
    includeUtilization: true,
    onlyVacancies
  }), [dateRange, selectedBuilding, selectedFloor, selectedRooms, selectedSessionTimes, selectedUsers, selectedStatuses, onlyVacancies]);
  
  const {
    data: plannerData,
    isLoading: gridLoading,
    error: gridError
  } = usePlannerGridData(apiFilters);
  
  const {
    data: filtersData
  } = usePlannerFilters();
  
  const {
    data: floorPlanData,
    isLoading: floorPlanLoading,
    error: floorPlanError
  } = useFloorPlanData({
    floorId: selectedFloor || '',
    date: format(dateRange[0], 'yyyy-MM-dd'), // Use start of date range for floor plan
    includeRooms: true,
    includeAssignments: true,
    includeBlocks: true,
    includeSessionTimes: true
  });
  
  // Fetch KPI data for inline toolbar display (ADDED: 08-10-2025 18:03:30 - Gap P0)
  const {
    data: kpisData
  } = useCoordinatorKPIs({
    startDate: format(dateRange[0], 'yyyy-MM-dd'),
    endDate: format(dateRange[1], 'yyyy-MM-dd'),
    ...(selectedBuilding && { buildingId: selectedBuilding })
  });
  
  // Fetch holidays for holiday indicators (ADDED: 08-10-2025 18:57:30)
  const {
    data: holidaysData
  } = useHolidays({
    ...(selectedBuilding && { buildingId: selectedBuilding }),
    year: dateRange[0].getFullYear(),
    month: dateRange[0].getMonth() + 1
  });
  
  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================
  
  const isLoading = gridLoading || floorPlanLoading;
  const hasError = gridError || floorPlanError;
  const errorMessage = (gridError as any)?.message || (floorPlanError as any)?.message || 'An error occurred';
  
  // Filter options from API data
  const filterOptions = useMemo(() => {
    if (!filtersData?.data) return { buildings: [], floors: [], rooms: [], sessionTimes: [], users: [] };
    
    const buildings = filtersData.data.buildings?.map((building: any) => ({
      value: building.id,
      label: building.name
    })) || [];
    
    const floors = filtersData.data.floors?.map((floor: any) => ({
      value: floor.id,
      label: `${floor.name} (${floor.buildingName || 'Unknown'})`,
      buildingId: floor.buildingId // CRITICAL: Preserve for filtering
    })) || [];
    
    const rooms = filtersData.data.rooms?.map((room: any) => ({
      value: room.id,
      label: `${room.name} (${room.floorName})`,
      floorId: room.floorId // CRITICAL: Preserve for filtering
    })) || [];
    
    const sessionTimes = filtersData.data.sessionTimes?.map((sessionTime: any) => ({
      value: sessionTime.id,
      label: `${sessionTime.name} (${sessionTime.startTime} - ${sessionTime.endTime})`,
      roomId: sessionTime.roomId,  // CRITICAL: Include roomId for filtering
      room: sessionTime.room  // Include room info for display
    })) || [];
    
    const users = filtersData.data.users?.map((user: any) => ({
      value: user.id,
      label: user.name
    })) || [];
    
    return { buildings, floors, rooms, sessionTimes, users };
  }, [filtersData]);
  
  // Active filters summary
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedBuilding) count++;
    if (selectedFloor) count++;
    if (selectedRooms.length > 0) count++;
    if (selectedSessionTimes.length > 0) count++;
    if (selectedUsers.length > 0) count++;
    if (selectedStatuses.length > 0) count++;
    if (onlyVacancies) count++;
    return count;
  }, [selectedBuilding, selectedFloor, selectedRooms, selectedSessionTimes, selectedUsers, selectedStatuses, onlyVacancies]);
  
  // NEW: Convert active filters to FilterChip array for ActiveFiltersChips component
  const activeFilterChips = useMemo((): FilterChip[] => {
    const chips: FilterChip[] = [];
    
    // Building filter
    if (selectedBuilding) {
      const building = filterOptions.buildings.find((b: any) => b.value === selectedBuilding);
      if (building) {
        chips.push({
          id: 'building',
          label: building.label,
          type: 'building',
          value: selectedBuilding
        });
      }
    }
    
    // Floor filter
    if (selectedFloor) {
      const floor = filterOptions.floors.find((f: any) => f.value === selectedFloor);
      if (floor) {
        chips.push({
          id: 'floor',
          label: floor.label,
          type: 'floor',
          value: selectedFloor
        });
      }
    }
    
    // Rooms filter
    if (selectedRooms.length > 0) {
      chips.push({
        id: 'rooms',
        label: `${selectedRooms.length} Room${selectedRooms.length > 1 ? 's' : ''}`,
        type: 'room',
        value: selectedRooms
      });
    }
    
    // Session times filter
    if (selectedSessionTimes.length > 0) {
      chips.push({
        id: 'sessionTimes',
        label: `${selectedSessionTimes.length} Session${selectedSessionTimes.length > 1 ? 's' : ''}`,
        type: 'sessionTime',
        value: selectedSessionTimes
      });
    }
    
    // Users filter
    if (selectedUsers.length > 0) {
      chips.push({
        id: 'users',
        label: `${selectedUsers.length} Doctor${selectedUsers.length > 1 ? 's' : ''}`,
        type: 'user',
        value: selectedUsers
      });
    }
    
    // Statuses filter
    if (selectedStatuses.length > 0) {
      chips.push({
        id: 'statuses',
        label: selectedStatuses.join(', '),
        type: 'status',
        value: selectedStatuses
      });
    }
    
    // Vacancies only filter
    if (onlyVacancies) {
      chips.push({
        id: 'vacancies',
        label: 'Only Vacancies',
        type: 'vacancies',
        value: true
      });
    }
    
    return chips;
  }, [selectedBuilding, selectedFloor, selectedRooms, selectedSessionTimes, selectedUsers, selectedStatuses, onlyVacancies, filterOptions]);
  
  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================
  
  const handleRefresh = useCallback(() => {
    invalidateAll();
    toastSystem.dataRefreshed();
  }, [invalidateAll]);
  
  const handleExport = useCallback(() => {
    setShowExportModal(true);
  }, []);
  
  const handleCreateAssignment = useCallback(() => {
    setShowAssignModal(true);
  }, []);
  
  // FIXED: 08-10-2025 19:03:31 - Handle assignment data from grid cell click
  const handleAssignmentData = useCallback((data: {
    roomId: string;
    sessionTemplateId: string;
    date: string;
  }) => {
    setAssignmentData(data);
    setShowAssignModal(true);
  }, []);
  
  const handleRoomClick = useCallback((room: any) => {
    setSidePanelContent({ type: 'room-details', data: room });
    setSidePanelOpen(true);
    setStoreSidePanelOpen(true);
    setStoreSidePanelContent('room-details');
  }, [setStoreSidePanelOpen, setStoreSidePanelContent]);
  
  /**
   * ADDED: 09-10-2025 12:12:58
   * Handle room double-click from Floor Plan Canvas
   * Opens assign modal with room pre-selected
   * 
   * @param room - Room object from floor plan
   */
  const handleRoomDoubleClick = useCallback((room: any) => {
    // Pre-fill assignment data with room and current date
    setAssignmentData({
      roomId: room.id,
      sessionTemplateId: '', // User will select session in modal
      date: format(dateRange[0], 'yyyy-MM-dd')
    });
    setShowAssignModal(true);
  }, [dateRange]);
  
  /**
   * ADDED: 09-10-2025 12:12:58
   * Handle context menu actions from Floor Plan Canvas
   * Supports: assign-doctor, create-block, view-schedule, open-in-grid
   * 
   * @param action - The context menu action type
   * @param room - Room object from floor plan
   */
  const handleContextMenuAction = useCallback((action: RoomContextAction, room: any) => {
    switch (action) {
      case 'assign-doctor':
        // Same as double-click - open assign modal
        setAssignmentData({
          roomId: room.id,
          sessionTemplateId: '',
          date: format(dateRange[0], 'yyyy-MM-dd')
        });
        setShowAssignModal(true);
        break;
      
      case 'create-block':
        // Open block modal with room pre-selected
        // TODO: Implement BlockModal integration when component is available
        setShowBlockModal(true);
        setSidePanelContent({ type: 'room-details', data: room });
        break;
      
      case 'view-schedule':
        // Open room details in side panel
        setSidePanelContent({ type: 'room-details', data: room });
        setSidePanelOpen(true);
        setStoreSidePanelOpen(true);
        setStoreSidePanelContent('room-details');
        break;
      
      case 'open-in-grid':
        // Switch to grid view and scroll to room
        setActiveView('grid');
        setStoreActiveView('grid');
        // Update URL with room ID for deep linking
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('roomId', room.id);
          newParams.set('view', 'grid');
          return newParams;
        });
        break;
      
      default:
        console.warn(`Unknown context menu action: ${action}`);
    }
  }, [dateRange, setStoreSidePanelOpen, setStoreSidePanelContent, setStoreActiveView, setSearchParams]);
  
  const handleAssignmentClick = useCallback((assignments: any) => {
    // Handle both single assignment and multiple assignments
    const assignmentData = Array.isArray(assignments) ? assignments : [assignments];
    setSidePanelContent({ type: 'assignment-details', data: assignmentData });
    setSidePanelOpen(true);
    setStoreSidePanelOpen(true);
    setStoreSidePanelContent('room-details');
  }, [setStoreSidePanelOpen, setStoreSidePanelContent]);
  
  // UPDATED: 10-10-2025 21:02:31 - Added 'calendar' view support
  const handleViewToggle = useCallback((view: 'grid' | 'floor-plan' | 'calendar') => {
    setActiveView(view);
    setStoreActiveView(view as any);
    
    if (view === 'floor-plan' && !selectedFloor) {
      toastSystem.viewChanged('floor-plan');
    } else if (view === 'calendar') {
      toastSystem.viewChanged('calendar');
    }
  }, [selectedFloor, setStoreActiveView]);
  
  // FIXED: 15-10-2025 18:45:00 - Use initialDate for calendar initialization, not dateRange[0]
  // dateRange[0] is the start of the week (Monday) when viewMode is 'week'
  // But calendar should initially open to TODAY's date, then RoomCalendarView can navigate to appropriate week/month based on viewMode
  // This matches user expectation: clicking "Calendar" opens to today, then user can navigate or switch views
  const stableInitialDate = useMemo(() => initialDate, []);
  
  // FIXED: 10-10-2025 22:35:00 - Prevent circular dependency by checking if range actually changed
  const handleCalendarDateRangeChange = useCallback((startDate: string, endDate: string) => {
    const newStart = new Date(startDate);
    const newEnd = new Date(endDate);

    // Check if the range actually changed to prevent unnecessary updates
    setDateRange(prevRange => {
      const currentStart = prevRange[0];
      const currentEnd = prevRange[1];

      // Compare dates (ignore time differences)
      const startChanged = currentStart.toDateString() !== newStart.toDateString();
      const endChanged = currentEnd.toDateString() !== newEnd.toDateString();

      if (!startChanged && !endChanged) {
        console.log('🔄 [EnhancedPlannerPage] Date range unchanged, skipping update');
        return prevRange; // Return same reference to prevent re-renders
      }

      console.log('🔄 [EnhancedPlannerPage] Date range changed:', {
        from: `${currentStart.toDateString()} to ${currentEnd.toDateString()}`,
        to: `${newStart.toDateString()} to ${newEnd.toDateString()}`
      });

      return [newStart, newEnd];
    });
  }, []); // Empty dependency array - stable reference
  
  const handleFilterChange = useCallback((key: string, value: any) => {
    switch (key) {
      case 'dateRange':
        setDateRange(value as DateRange);
        break;
      case 'viewMode':
        setViewMode(value as ViewMode);
        // Recalculate date range based on new view mode
        setDateRange(calculateDateRange(value as ViewMode, dateRange[0]));
        break;
      case 'selectedBuilding':
        setSelectedBuilding(value as string | null);
        setSelectedFloor(null); // Reset floor when building changes
        setSelectedRooms([]); // Reset rooms when building changes
        setStoreSelectedFloor(undefined);
        break;
      case 'selectedFloor':
        setSelectedFloor(value as string | null);
        setSelectedRooms([]); // Reset rooms when floor changes
        setStoreSelectedFloor(value as string | undefined);
        break;
      case 'selectedRooms':
        setSelectedRooms(value as string[]);
        break;
      case 'selectedSessionTimes':
        setSelectedSessionTimes(value as string[]);
        break;
      case 'selectedUsers':
        setSelectedUsers(value as string[]);
        break;
      case 'selectedStatuses':
        setSelectedStatuses(value as string[]);
        break;
      case 'onlyVacancies':
        setOnlyVacancies(value as boolean);
        break;
    }
  }, [setStoreSelectedFloor]);
  
  
  const handleBulkAssign = useCallback(async (selections: any[], doctorId: string) => {
    try {
      // TODO: Implement bulk assignment API call
      console.log('Bulk assign:', { selections, doctorId });
      
      notifications.show({
        title: 'Bulk Assignment Successful',
        message: `Successfully assigned ${selections.length} slots`,
        color: 'green',
        icon: <IconCheck size={16} />
      });
      
      // Refresh data
      invalidateAll();
    } catch (error) {
      notifications.show({
        title: 'Bulk Assignment Failed',
        message: error instanceof Error ? error.message : 'Failed to assign slots',
        color: 'red',
        icon: <IconX size={16} />
      });
    }
  }, [invalidateAll]);
  
  const handleBulkUnassign = useCallback(async (selections: any[]) => {
    try {
      // Store deleted data for undo functionality
      const deletedAssignments = [...selections];
      
      // TODO: Implement bulk unassignment API call
      console.log('Bulk unassign:', { selections });
      
      // Register undo operation with 5-second window
      registerUndo('bulk-unassignment', deletedAssignments, async () => {
        // Undo function: restore the deleted assignments
        console.log('Restoring assignments:', deletedAssignments);
        // TODO: Implement restore API call
        invalidateAll();
        notifications.show({
          title: 'Undo Successful',
          message: `Restored ${deletedAssignments.length} assignments`,
          color: 'blue',
          icon: <IconCheck size={16} />
        });
      });
      
      // Show undo toast with action button (UPDATED: 08-10-2025 18:03:30 - Gap P1)
      notifications.show({
        id: 'undo-toast',
        title: 'Assignments Removed',
        message: (
          <Group gap="sm" align="center">
            <Text size="sm">
              {selections.length} assignments deleted. Undo available for {remainingSeconds}s
            </Text>
            {canUndo && (
              <Button 
                size="xs" 
                variant="light" 
                color="orange"
                onClick={() => {
                  executeUndo();
                  notifications.hide('undo-toast');
                }}
              >
                Undo ({remainingSeconds}s)
              </Button>
            )}
          </Group>
        ),
        color: 'orange',
        icon: <IconAlertTriangle size={16} />,
        autoClose: 5000,
        withCloseButton: true
      });
      
      // Refresh data
      invalidateAll();
    } catch (error) {
      notifications.show({
        title: 'Bulk Unassignment Failed',
        message: error instanceof Error ? error.message : 'Failed to remove assignments',
        color: 'red',
        icon: <IconX size={16} />
      });
    }
  }, [invalidateAll, registerUndo, canUndo, remainingSeconds, executeUndo]);
  
  // Update undo toast countdown (ADDED: 08-10-2025 18:03:30 - Gap P1)
  useEffect(() => {
    if (canUndo && remainingSeconds > 0) {
      notifications.update({
        id: 'undo-toast',
        message: `Undo available for ${remainingSeconds}s`,
      });
    }
  }, [remainingSeconds, canUndo]);
  
  // Sync URL params when filters change (ADDED: 08-10-2025 18:03:30 - Gap P1)
  useEffect(() => {
    const params = new URLSearchParams();
    
    // Preserve roomId parameter for drawer functionality
    const currentRoomId = searchParams.get('roomId');
    if (currentRoomId) {
      params.set('roomId', currentRoomId);
    }
    
    if (selectedBuilding) params.set('building', selectedBuilding);
    if (selectedFloor) params.set('floor', selectedFloor);
    if (selectedRooms.length > 0) params.set('rooms', selectedRooms.join(','));
    if (selectedSessionTimes.length > 0) params.set('sessions', selectedSessionTimes.join(','));
    if (selectedUsers.length > 0) params.set('users', selectedUsers.join(','));
    if (selectedStatuses.length > 0) params.set('statuses', selectedStatuses.join(','));
    if (onlyVacancies) params.set('onlyVacancies', 'true');
    
    setSearchParams(params, { replace: true });
  }, [selectedBuilding, selectedFloor, selectedRooms, selectedSessionTimes, selectedUsers, selectedStatuses, onlyVacancies, setSearchParams, searchParams]);
  
  // No filter persistence - clean state every session
  
  const handleCopySchedule = useCallback(() => {
    setShowCopyModal(true);
  }, []);
  
  const handleRecurrenceSchedule = useCallback(() => {
    setShowRecurrenceModal(true);
  }, []);
  
  const handlePublishSchedule = useCallback(() => {
    setShowPublishModal(true);
  }, []);
  
  const clearAllFilters = useCallback(() => {
    setSelectedBuilding(null);
    setSelectedFloor(null);
    setSelectedRooms([]);
    setSelectedSessionTimes([]);
    setSelectedUsers([]);
    setSelectedStatuses([]);
    setOnlyVacancies(false);
    
    notifications.show({
      title: 'Filters Cleared',
      message: 'All filters have been reset',
      color: 'blue',
      icon: <IconX size={16} />
    });
  }, []);
  
  // NEW: Remove individual filter chip
  const handleRemoveFilterChip = useCallback((filterId: string) => {
    switch (filterId) {
      case 'building':
        setSelectedBuilding(null);
        setSelectedFloor(null); // Also clear floor when building removed
        setStoreSelectedFloor(undefined);
        break;
      case 'floor':
        setSelectedFloor(null);
        setStoreSelectedFloor(undefined);
        break;
      case 'rooms':
        setSelectedRooms([]);
        break;
      case 'sessionTimes':
        setSelectedSessionTimes([]);
        break;
      case 'users':
        setSelectedUsers([]);
        break;
      case 'statuses':
        setSelectedStatuses([]);
        break;
      case 'vacancies':
        setOnlyVacancies(false);
        break;
    }
    
    notifications.show({
      title: 'Filter Removed',
      message: 'Filter has been cleared',
      color: 'blue',
      autoClose: 2000
    });
  }, [setStoreSelectedFloor]);
  
  // =============================================================================
  // RENDER HELPERS
  // =============================================================================
  
  const renderSidePanel = () => {
    if (!sidePanelOpen) return null;
    
    return (
      <Drawer
        opened={sidePanelOpen}
        onClose={() => setSidePanelOpen(false)}
        title={
          <Group gap="sm">
            <Text fw={600}>
              {sidePanelContent.type === 'room-details' && 'Room Details'}
              {sidePanelContent.type === 'assignment-details' && 'Assignment Details'}
              {sidePanelContent.type === 'conflicts' && 'Conflicts'}
              {sidePanelContent.type === 'utilization' && 'Utilization'}
            </Text>
          </Group>
        }
        size="md"
        position="right"
        withOverlay
        overlayProps={{ 
          opacity: 0.3, 
          blur: 4,
          backgroundOpacity: 0.3
        }}
        styles={{
          content: {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)'
          }
        }}
      >
        <Stack gap="md">
          {sidePanelContent.type === 'room-details' && sidePanelContent.data && (
            <Box>
              <Stack gap="md">
                {/* Room Header */}
                <Paper p="md" radius="md" withBorder style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
                  <Group justify="space-between" align="flex-start">
                    <Stack gap="xs">
                      <Group gap="sm">
                        <IconMapPin size={20} color="var(--mantine-color-blue-6)" />
                        <Text fw={700} size="lg" c="dark">
                          {sidePanelContent.data.number} - {sidePanelContent.data.name}
                        </Text>
                      </Group>
                      <Badge 
                        color={sidePanelContent.data.isActive ? 'green' : 'red'}
                        variant="light"
                        size="lg"
                      >
                        {sidePanelContent.data.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </Stack>
                    <Group gap="xs">
                      <Tooltip label="Edit room details">
                        <Button 
                          size="xs" 
                          variant="light" 
                          color="blue"
                          onClick={() => {
                            // TODO: Implement room edit functionality
                            notifications.show({
                              title: 'Edit Room',
                              message: 'Room editing functionality will be implemented',
                              color: 'blue'
                            });
                          }}
                        >
                          <IconEdit size={14} />
                        </Button>
                      </Tooltip>
                      <Tooltip label="Delete room">
                        <Button 
                          size="xs" 
                          variant="light" 
                          color="red"
                          onClick={() => {
                            // TODO: Implement room delete functionality
                            notifications.show({
                              title: 'Delete Room',
                              message: 'Room deletion functionality will be implemented',
                              color: 'red'
                            });
                          }}
                        >
                          <IconTrash size={14} />
                        </Button>
                      </Tooltip>
                    </Group>
                  </Group>
                </Paper>

                {/* Room Information */}
                <Paper p="md" radius="md" withBorder>
                  <Text fw={600} size="md" c="dark" mb="sm">
                    Room Information
                  </Text>
                  <Stack gap="xs">
                    <Group>
                      <Text fw={500} size="sm" c="dimmed" style={{ width: '100px' }}>Capacity:</Text>
                      <Badge 
                        size="sm" 
                        variant="light" 
                        color="blue"
                        style={{ fontWeight: 600 }}
                      >
                        {sidePanelContent.data.capacity} People
                      </Badge>
                    </Group>
                    <Group>
                      <Text fw={500} size="sm" c="dimmed" style={{ width: '100px' }}>Building:</Text>
                      <Text fw={500} size="sm">{sidePanelContent.data.floor?.building?.name || 'N/A'}</Text>
                    </Group>
                    <Group>
                      <Text fw={500} size="sm" c="dimmed" style={{ width: '100px' }}>Floor:</Text>
                      <Text fw={500} size="sm">{sidePanelContent.data.floor?.name || 'N/A'}</Text>
                    </Group>
                    <Group>
                      <Text fw={500} size="sm" c="dimmed" style={{ width: '100px' }}>Status:</Text>
                      <Badge 
                        size="sm" 
                        variant="light" 
                        color={sidePanelContent.data.isActive ? 'green' : 'red'}
                      >
                        {sidePanelContent.data.isActive ? 'Available' : 'Out of Service'}
                      </Badge>
                    </Group>
                  </Stack>
                </Paper>

                {/* Today's Assignments */}
                <Paper p="md" radius="md" withBorder>
                  <Group justify="space-between" align="center" mb="sm">
                    <Text fw={600} size="md" c="dark">
                      Today's Assignments
                    </Text>
                    <Badge 
                      color="blue"
                      variant="light"
                      size="lg"
                    >
                      {(() => {
                        const todayAssignments = plannerData?.data?.assignments?.filter(
                          (assignment: any) => assignment.roomId === sidePanelContent.data.id && 
                          assignment.date.startsWith(format(dateRange[0], 'yyyy-MM-dd'))
                        ) || [];
                        return todayAssignments.length;
                      })()}
                    </Badge>
                  </Group>
                  
                  {(() => {
                    const todayAssignments = plannerData?.data?.assignments?.filter(
                      (assignment: any) => assignment.roomId === sidePanelContent.data.id && 
                      assignment.date.startsWith(format(dateRange[0], 'yyyy-MM-dd'))
                    ) || [];
                    
                    if (todayAssignments.length === 0) {
                      return (
                        <Text size="sm" c="dimmed" ta="center" py="md">
                          No assignments for selected date
                        </Text>
                      );
                    }
                    
                    return (
                      <Stack gap="sm">
                        {todayAssignments.map((assignment: any, index: number) => (
                          <Paper key={assignment.id} p="sm" radius="md" withBorder style={{ backgroundColor: '#f8f9fa' }}>
                            <Stack gap="xs">
                              <Group justify="space-between" align="flex-start">
                                <Stack gap="xs">
                                  <Text fw={600} size="sm" c="dark">
                                    Assignment #{index + 1}
                                  </Text>
                                  <Badge 
                                    color="green"
                                    variant="light"
                                    size="sm"
                                  >
                                    {assignment.status}
                                  </Badge>
                                </Stack>
                                <Group gap="xs">
                                  <Button size="xs" variant="light" color="blue">
                                    <IconEdit size={12} />
                                  </Button>
                                  <Button size="xs" variant="light" color="red">
                                    <IconTrash size={12} />
                                  </Button>
                                </Group>
                              </Group>
                              
                              <Divider />
                              
                              <Stack gap="xs">
                                <Group>
                                  <Text fw={500} size="xs" c="dimmed" style={{ width: '60px' }}>Doctor:</Text>
                                  <Text fw={500} size="xs">{assignment.user?.name || 'N/A'}</Text>
                                </Group>
                                <Group>
                                  <Text fw={500} size="xs" c="dimmed" style={{ width: '60px' }}>Session:</Text>
                                  <Text fw={500} size="xs">{assignment.sessionTime?.name || 'N/A'}</Text>
                                </Group>
                                <Group>
                                  <Text fw={500} size="xs" c="dimmed" style={{ width: '60px' }}>Time:</Text>
                                  <Text fw={500} size="xs">
                                    {assignment.sessionTime?.startTime || 'N/A'} - {assignment.sessionTime?.endTime || 'N/A'}
                                  </Text>
                                </Group>
                                <Group>
                                  <Text fw={500} size="xs" c="dimmed" style={{ width: '60px' }}>Title:</Text>
                                  <Text fw={500} size="xs">{assignment.title || 'No title'}</Text>
                                </Group>
                              </Stack>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    );
                  })()}
                </Paper>

                {/* Room Utilization */}
                <Paper p="md" radius="md" withBorder>
                  <Text fw={600} size="md" c="dark" mb="sm">
                    Room Utilization
                  </Text>
                  {(() => {
                    const roomAssignments = plannerData?.data?.assignments?.filter(
                      (assignment: any) => assignment.roomId === sidePanelContent.data.id && 
                      assignment.date.startsWith(format(dateRange[0], 'yyyy-MM-dd'))
                    ) || [];
                    const assignedCount = roomAssignments.length;
                    const utilizationRate = sidePanelContent.data.capacity > 0 ? 
                      Math.round((assignedCount / sidePanelContent.data.capacity) * 100) : 0;
                    
                    return (
                      <Stack gap="md">
                        <Group justify="space-between">
                          <Text fw={500} size="sm">Current Utilization</Text>
                          <Text fw={600} size="lg" c={utilizationRate >= 80 ? 'red' : utilizationRate >= 50 ? 'yellow' : 'green'}>
                            {utilizationRate}%
                          </Text>
                        </Group>
                        
                        <Group gap="xs">
                          <Badge 
                            size="sm" 
                            variant="light" 
                            color={utilizationRate >= 80 ? 'red' : utilizationRate >= 50 ? 'yellow' : 'green'}
                            style={{ fontWeight: 600 }}
                          >
                            {assignedCount}/{sidePanelContent.data.capacity} Assigned
                          </Badge>
                          <Badge 
                            size="sm" 
                            variant="light" 
                            color="gray"
                            style={{ fontWeight: 600 }}
                          >
                            {sidePanelContent.data.capacity - assignedCount} Available
                          </Badge>
                        </Group>
                        
                        {/* Utilization Bar */}
                        <Box>
                          <Group justify="space-between" mb="xs">
                            <Text size="xs" c="dimmed">Utilization</Text>
                            <Text size="xs" c="dimmed">{utilizationRate}%</Text>
                          </Group>
                          <Box 
                            style={{ 
                              height: '8px', 
                              backgroundColor: '#e2e8f0', 
                              borderRadius: '4px',
                              overflow: 'hidden'
                            }}
                          >
                            <Box
                              style={{
                                height: '100%',
                                width: `${Math.min(utilizationRate, 100)}%`,
                                backgroundColor: utilizationRate >= 80 ? '#ef4444' : utilizationRate >= 50 ? '#f59e0b' : '#10b981',
                                transition: 'width 0.3s ease'
                              }}
                            />
                          </Box>
                        </Box>
                      </Stack>
                    );
                  })()}
                </Paper>

                {/* Session Times */}
                <Paper p="md" radius="md" withBorder>
                  <Text fw={600} size="md" c="dark" mb="sm">
                    Available Session Times
                  </Text>
                  <Stack gap="xs">
                    {sidePanelContent.data.sessionTimes?.map((sessionTime: any) => (
                      <Group key={sessionTime.id} justify="space-between" p="sm" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                        <Stack gap="xs">
                          <Text fw={500} size="sm">{sessionTime.name}</Text>
                          <Text size="xs" c="dimmed">
                            {sessionTime.startTime} - {sessionTime.endTime}
                          </Text>
                        </Stack>
                        <Badge 
                          size="sm" 
                          variant="light" 
                          color={sessionTime.isActive ? 'green' : 'red'}
                        >
                          {sessionTime.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </Group>
                    )) || (
                      <Text size="sm" c="dimmed" ta="center" py="md">
                        No session times configured
                      </Text>
                    )}
                  </Stack>
                </Paper>

                {/* Quick Actions */}
                <Paper p="md" radius="md" withBorder style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}>
                  <Text fw={600} size="md" c="dark" mb="sm">
                    Quick Actions
                  </Text>
                  <Group gap="sm">
                    <Tooltip label="Create new assignment for this room">
                      <Button 
                        size="sm" 
                        variant="light" 
                        color="blue" 
                        leftSection={<IconPlus size={16} />}
                        onClick={handleCreateAssignment}
                      >
                        Create Assignment
                      </Button>
                    </Tooltip>
                    <Tooltip label="View room schedule and assignments">
                      <Button 
                        size="sm" 
                        variant="light" 
                        color="green" 
                        leftSection={<IconCalendar size={16} />}
                        onClick={() => {
                          // TODO: Implement view schedule functionality
                          notifications.show({
                            title: 'View Schedule',
                            message: 'Schedule viewing functionality will be implemented',
                            color: 'green'
                          });
                        }}
                      >
                        View Schedule
                      </Button>
                    </Tooltip>
                    <Tooltip label="Configure room settings and session times">
                      <Button 
                        size="sm" 
                        variant="light" 
                        color="orange" 
                        leftSection={<IconSettings size={16} />}
                        onClick={() => {
                          // TODO: Implement room configuration functionality
                          notifications.show({
                            title: 'Configure Room',
                            message: 'Room configuration functionality will be implemented',
                            color: 'orange'
                          });
                        }}
                      >
                        Configure
                      </Button>
                    </Tooltip>
                  </Group>
                </Paper>
              </Stack>
            </Box>
          )}
          
          {sidePanelContent.type === 'assignment-details' && (
            <Box>
              {sidePanelContent.data && Array.isArray(sidePanelContent.data) ? (
                <Stack gap="md">
                  {/* Assignment Header */}
                  <Paper p="md" radius="md" withBorder style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
                    <Group justify="space-between" align="flex-start">
                      <Stack gap="xs">
                        <Text fw={700} size="lg" c="dark">
                          Assignment Details
                        </Text>
                        <Badge 
                          color="blue"
                          variant="filled"
                          size="lg"
                        >
                          {sidePanelContent.data.length} Assignment{sidePanelContent.data.length > 1 ? 's' : ''}
                        </Badge>
                      </Stack>
                      <Group gap="xs">
                        <Button size="xs" variant="light" color="blue">
                          Edit All
                        </Button>
                        <Button size="xs" variant="light" color="red">
                          Delete All
                        </Button>
                      </Group>
                    </Group>
                  </Paper>

                  {/* Room & Session Information */}
                  <Paper p="md" radius="md" withBorder>
                    <Text fw={600} size="md" c="dark" mb="sm">
                      Room & Session Details
                    </Text>
                    <Stack gap="xs">
                      <Group>
                        <Text fw={500} size="sm" c="dimmed" style={{ width: '100px' }}>Room:</Text>
                        <Text fw={500}>{sidePanelContent.data[0]?.room?.name || 'N/A'} ({sidePanelContent.data[0]?.room?.number || 'N/A'})</Text>
                      </Group>
                      <Group>
                        <Text fw={500} size="sm" c="dimmed" style={{ width: '100px' }}>Session:</Text>
                        <Text fw={500}>{sidePanelContent.data[0]?.sessionTime?.name || 'N/A'}</Text>
                      </Group>
                      <Group>
                        <Text fw={500} size="sm" c="dimmed" style={{ width: '100px' }}>Time:</Text>
                        <Text fw={500}>
                          {sidePanelContent.data[0]?.sessionTime?.startTime || 'N/A'} - {sidePanelContent.data[0]?.sessionTime?.endTime || 'N/A'}
                        </Text>
                      </Group>
                      <Group>
                        <Text fw={500} size="sm" c="dimmed" style={{ width: '100px' }}>Date:</Text>
                        <Text fw={500}>
                          {sidePanelContent.data[0]?.date ? new Date(sidePanelContent.data[0].date).toLocaleDateString() : 'N/A'}
                        </Text>
                      </Group>
                    </Stack>
                  </Paper>

                  {/* All Assignments List */}
                  <Paper p="md" radius="md" withBorder>
                    <Text fw={600} size="md" c="dark" mb="sm">
                      All Assignments
                    </Text>
                    <Stack gap="md">
                      {sidePanelContent.data.map((assignment, index) => (
                        <Paper key={assignment.id} p="md" radius="md" withBorder style={{ backgroundColor: '#f8f9fa' }}>
                          <Stack gap="xs">
                            <Group justify="space-between" align="flex-start">
                              <Stack gap="xs">
                                <Text fw={600} size="sm" c="dark">
                                  Assignment #{index + 1}
                                </Text>
                                <Badge 
                                  color={assignment.status === 'PENDING' ? 'yellow' : 'green'}
                                  variant="light"
                                  size="sm"
                                >
                                  {assignment.status}
                                </Badge>
                              </Stack>
                              <Group gap="xs">
                                <Button size="xs" variant="light" color="blue">
                                  Edit
                                </Button>
                                <Button size="xs" variant="light" color="red">
                                  Delete
                                </Button>
                              </Group>
                            </Group>
                            
                            <Divider />
                            
                            <Stack gap="xs">
                              <Group>
                                <Text fw={500} size="sm" c="dimmed" style={{ width: '80px' }}>Doctor:</Text>
                                <Text fw={500} size="sm">{assignment.user?.name || 'N/A'}</Text>
                                <Badge size="xs" variant="light" color="blue">
                                  {assignment.user?.role || 'N/A'}
                                </Badge>
                              </Group>
                              <Group>
                                <Text fw={500} size="sm" c="dimmed" style={{ width: '80px' }}>Title:</Text>
                                <Text fw={500} size="sm">{assignment.title || 'N/A'}</Text>
                              </Group>
                              <Group>
                                <Text fw={500} size="sm" c="dimmed" style={{ width: '80px' }}>Description:</Text>
                                <Text fw={500} size="sm">{assignment.description || 'No description'}</Text>
                              </Group>
                              <Group>
                                <Text fw={500} size="sm" c="dimmed" style={{ width: '80px' }}>Created:</Text>
                                <Text fw={500} size="sm">
                                  {assignment.createdAt ? new Date(assignment.createdAt).toLocaleString() : 'N/A'}
                                </Text>
                              </Group>
                            </Stack>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Paper>

                  {/* Actions */}
                  <Paper p="md" radius="md" withBorder style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}>
                    <Text fw={600} size="md" c="dark" mb="sm">
                      Quick Actions
                    </Text>
                    <Group gap="sm">
                      <Button size="sm" variant="light" color="blue">
                        Edit All Assignments
                      </Button>
                      <Button size="sm" variant="light" color="green">
                        Mark All Complete
                      </Button>
                      <Button size="sm" variant="light" color="orange">
                        Reschedule All
                      </Button>
                      <Button size="sm" variant="light" color="red">
                        Cancel All Assignments
                      </Button>
                    </Group>
                  </Paper>
                </Stack>
              ) : (
                <Text size="sm" c="dimmed">
                  No assignment selected. Click on an assignment in the grid to view details.
                </Text>
              )}
            </Box>
          )}
          
          {sidePanelContent.type === 'conflicts' && (
            <Box>
              <Text size="sm" c="dimmed">
                Conflicts panel will be implemented with:
                - Conflict list and details
                - Resolution suggestions
                - Bulk conflict resolution
                - Conflict history
              </Text>
            </Box>
          )}
          
          {sidePanelContent.type === 'utilization' && (
            <Box>
              <Text size="sm" c="dimmed">
                Utilization panel will be implemented with:
                - Room utilization charts
                - Time-based analytics
                - Capacity metrics
                - Performance indicators
              </Text>
            </Box>
          )}
        </Stack>
      </Drawer>
    );
  };
  
  const renderFilters = () => {
    return (
      <ExpandablePanel expanded={showFilters} duration={200}>
        <Paper 
          p="md" 
          radius="md" 
          withBorder
          style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            boxShadow: showFilters ? '0 2px 8px rgba(0, 0, 0, 0.08)' : 'none',
          }}
        >
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Group gap="sm">
              <IconFilter size={20} color="#1e40af" />
              <Text fw={600} size="lg" c="dark">
                Advanced Filters
              </Text>
              {activeFiltersCount > 0 && (
                <Badge 
                  size="sm" 
                  variant="filled" 
                  color="blue"
                  style={{ 
                    background: '#1e40af',
                    fontWeight: 500
                  }}
                >
                  {activeFiltersCount} Active
                </Badge>
              )}
            </Group>
            <Group gap="xs">
              
              <Tooltip label="Clear all active filters" position="bottom" withArrow>
                <Button
                  size="xs"
                  variant="light"
                  onClick={clearAllFilters}
                  disabled={activeFiltersCount === 0}
                  style={{
                    background: activeFiltersCount > 0 ? '#fef2f2' : '#f8fafc',
                    color: activeFiltersCount > 0 ? '#dc2626' : '#9ca3af',
                    fontWeight: 600
                  }}
                >
                  Clear All
                </Button>
              </Tooltip>
              <Tooltip label="Close filters panel" position="bottom" withArrow>
                <ActionIcon
                  variant="light"
                  onClick={() => setShowFilters(false)}
                  style={{
                    background: '#f1f5f9',
                    color: '#64748b'
                  }}
                >
                  <IconX size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
          
          <Grid>
            <Grid.Col span={3}>
              <Box>
                <Tooltip label="Select date range to view schedule (supports multi-day selection)" position="top" withArrow>
                <Text size="sm" fw={500} mb="xs" c="dark">
                  Date Range
                </Text>
                </Tooltip>
                <DatePickerInput
                  type="range"
                  value={dateRange}
                  onChange={(value) => {
                    if (value && value[0] && value[1]) {
                      handleFilterChange('dateRange', value as DateRange);
                    }
                  }}
                  size="sm"
                  clearable={false}
                  placeholder="Select date range"
                  aria-label="Select date range for schedule viewing"
                />
              </Box>
            </Grid.Col>
            
            <Grid.Col span={3}>
              <Box>
                <Tooltip label="Filter by specific building" position="top" withArrow>
                <Text size="sm" fw={500} mb="xs" c="dark">
                  Building
                </Text>
                </Tooltip>
                <Select
                  placeholder="All buildings"
                  data={filterOptions.buildings}
                  value={selectedBuilding}
                  onChange={(value) => handleFilterChange('selectedBuilding', value)}
                  clearable
                  size="sm"
                />
              </Box>
            </Grid.Col>
            
            <Grid.Col span={3}>
              <Box>
                <Tooltip label="Filter by specific floor" position="top" withArrow>
                <Text size="sm" fw={500} mb="xs" c="dark">
                  Floor
                </Text>
                </Tooltip>
                <Select
                  placeholder={selectedBuilding ? "Select Floor" : "Select Building First"}
                  data={selectedBuilding ? filterOptions.floors.filter((f: any) => f.buildingId === selectedBuilding) : []}
                  value={selectedFloor}
                  onChange={(value) => handleFilterChange('selectedFloor', value)}
                  clearable
                  size="sm"
                  disabled={!selectedBuilding}
                />
              </Box>
            </Grid.Col>
            
            <Grid.Col span={3}>
              <Box>
                <Tooltip label="Show only empty time slots" position="top" withArrow>
                <Text size="sm" fw={500} mb="xs" c="dark">
                  Options
                </Text>
                </Tooltip>
                <Switch
                  label="Only Vacancies"
                  checked={onlyVacancies}
                  onChange={(e) => handleFilterChange('onlyVacancies', e.currentTarget.checked)}
                  size="sm"
                />
              </Box>
            </Grid.Col>
          </Grid>
          
          <Grid>
            <Grid.Col span={4}>
              <Box>
                <Tooltip label="Select specific rooms to view" position="top" withArrow>
                <Text size="sm" fw={500} mb="xs" c="dark">
                  Rooms
                </Text>
                </Tooltip>
                <MultiSelect
                  placeholder={selectedFloor ? "Select Rooms" : "Select Floor First"}
                  data={selectedFloor ? filterOptions.rooms.filter((r: any) => r.floorId === selectedFloor) : []}
                  value={selectedRooms}
                  onChange={(value) => handleFilterChange('selectedRooms', value)}
                  clearable
                  size="sm"
                  disabled={!selectedFloor}
                />
              </Box>
            </Grid.Col>
            
            <Grid.Col span={4}>
              <Box>
                <Tooltip label="Filter by specific session times" position="top" withArrow>
                <Text size="sm" fw={500} mb="xs" c="dark">
                  Session Times
                </Text>
                </Tooltip>
                <MultiSelect
                  placeholder="All sessions"
                  data={filterOptions.sessionTimes}
                  value={selectedSessionTimes}
                  onChange={(value) => handleFilterChange('selectedSessionTimes', value)}
                  clearable
                  size="sm"
                />
              </Box>
            </Grid.Col>
            
            <Grid.Col span={4}>
              <Box>
                <Tooltip label="Filter by specific doctors/users" position="top" withArrow>
                <Text size="sm" fw={500} mb="xs" c="dark">
                  Users
                </Text>
                </Tooltip>
                <MultiSelect
                  placeholder="All users"
                  data={filterOptions.users}
                  value={selectedUsers}
                  onChange={(value) => handleFilterChange('selectedUsers', value)}
                  clearable
                  size="sm"
                />
              </Box>
            </Grid.Col>
          </Grid>
        </Stack>
      </Paper>
      </ExpandablePanel>
    );
  };
  
  const renderToolbar = () => {
    return (
      <Paper 
        p="md" 
        radius="md" 
        withBorder
        style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Group justify="space-between" align="center">
          {/* Left Section - Title and Stats */}
          <Group gap="md">
            <Group gap="sm">
              <IconBuilding size={24} color="#1e40af" />
              <Text fw={700} size="xl" c="dark">
                Hospital Room Scheduler
              </Text>
            </Group>
            <Group gap="xs">
              <Tooltip label="Total number of available rooms" position="bottom" withArrow>
                <Badge 
                  variant="filled" 
                  color="white" 
                  leftSection={<IconBuilding size={12} />}
                  style={{ 
                    background: '#ffffff',
                    color: '#1e40af',
                    fontWeight: 600,
                    border: '1px solid #e2e8f0'
                  }}
                >
                  {plannerData?.data?.rooms?.length || 0} Rooms
                </Badge>
              </Tooltip>
              <Tooltip label="Total active assignments" position="bottom" withArrow>
                <Badge 
                  variant="filled" 
                  color="white" 
                  leftSection={<IconUsers size={12} />}
                  style={{ 
                    background: '#ffffff',
                    color: '#1e40af',
                    fontWeight: 600,
                    border: '1px solid #e2e8f0'
                  }}
                >
                  {plannerData?.data?.assignments?.length || 0} Assignments
                </Badge>
              </Tooltip>
              {activeFiltersCount > 0 && (
                <Tooltip label="Active filter count" position="bottom" withArrow>
                  <Badge 
                    variant="filled" 
                    color="white" 
                    leftSection={<IconFilter size={12} />}
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      color: '#1e40af',
                      fontWeight: 600
                    }}
                  >
                    {activeFiltersCount} Filters
                  </Badge>
                </Tooltip>
              )}
            </Group>
            
            {/* Inline KPI Summary Strip (ADDED: 08-10-2025 18:03:30 - Gap P0) */}
            {kpisData && (
              <Group 
                gap="xs" 
                p="xs" 
                style={{ 
                  borderRadius: 8, 
                  background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
                  border: '1px solid #93C5FD',
                  boxShadow: '0 1px 2px rgba(59, 130, 246, 0.1)'
                }}
              >
                <Tooltip label="Room Utilization Rate" position="bottom" withArrow>
                  <Badge 
                    size="lg" 
                    variant="light" 
                    color="blue" 
                    leftSection={<IconChartBar size={14} />}
                    style={{ fontWeight: 600 }}
                  >
                    {kpisData.utilization}%
                  </Badge>
                </Tooltip>
                <Tooltip label="Vacant Session Slots" position="bottom" withArrow>
                  <Badge 
                    size="lg" 
                    variant="light" 
                    color="orange" 
                    leftSection={<IconClock size={14} />}
                    style={{ fontWeight: 600 }}
                  >
                    {kpisData.vacantSessions}
                  </Badge>
                </Tooltip>
                <Tooltip label="Active Scheduling Conflicts" position="bottom" withArrow>
                  <Badge 
                    size="lg" 
                    variant="light" 
                    color="red" 
                    leftSection={<IconAlertCircle size={14} />}
                    style={{ fontWeight: 600 }}
                  >
                    {kpisData.conflicts}
                  </Badge>
                </Tooltip>
              </Group>
            )}
          </Group>
          
          {/* Center Section - View Toggle (UPDATED: 08-10-2025 - Added view mode toggle) */}
          <Group gap="md">
            {/* View Mode Toggle (Day/Week/Month) */}
            <ViewModeToggle
              value={viewMode}
              onChange={(mode) => handleFilterChange('viewMode', mode)}
              size="sm"
            />
            
            <Divider orientation="vertical" />
            
            {/* Grid/Floor Plan/Calendar Toggle - UPDATED: 10-10-2025 21:02:31 */}
            <Button.Group>
              <Tooltip label="Switch to Grid View - See all rooms in a table format" position="bottom" withArrow>
                <Button
                  variant={activeView === 'grid' ? 'filled' : 'light'}
                  leftSection={<IconGrid3x3 size={16} />}
                  onClick={() => handleViewToggle('grid')}
                  size="sm"
                  style={{
                    background: activeView === 'grid' ? '#1e40af' : '#ffffff',
                    color: activeView === 'grid' ? 'white' : '#64748b',
                    fontWeight: 500,
                    border: '1px solid #e2e8f0'
                  }}
                >
                  Grid View
                </Button>
              </Tooltip>
              <Tooltip label="Switch to Calendar View - Day/Week/Month views" position="bottom" withArrow>
                <Button
                  variant={activeView === 'calendar' ? 'filled' : 'light'}
                  leftSection={<IconCalendar size={16} />}
                  onClick={() => handleViewToggle('calendar')}
                  size="sm"
                  style={{
                    background: activeView === 'calendar' ? '#1e40af' : '#ffffff',
                    color: activeView === 'calendar' ? 'white' : '#64748b',
                    fontWeight: 500,
                    border: '1px solid #e2e8f0'
                  }}
                >
                  Calendar View
                </Button>
              </Tooltip>
              <Tooltip label={!selectedFloor ? "Please select a floor first to view floor plan" : "Switch to Floor Plan view - Visual room layout"} position="bottom" withArrow>
                <Button
                  variant={activeView === 'floor-plan' ? 'filled' : 'light'}
                  leftSection={<IconMapPin size={16} />}
                  onClick={() => {
                    if (!selectedFloor && filterOptions.floors.length > 0) {
                      // Auto-select first floor if none selected
                      handleFilterChange('selectedFloor', filterOptions.floors[0].value);
                    }
                    handleViewToggle('floor-plan');
                  }}
                  size="sm"
                  disabled={!selectedFloor && filterOptions.floors.length === 0}
                  style={{
                    background: activeView === 'floor-plan' ? '#1e40af' : '#ffffff',
                    color: activeView === 'floor-plan' ? 'white' : '#64748b',
                    fontWeight: 500,
                    border: '1px solid #e2e8f0',
                    opacity: (!selectedFloor && filterOptions.floors.length === 0) ? 0.5 : 1
                  }}
                >
                  Floor Plan
                </Button>
              </Tooltip>
            </Button.Group>
          </Group>
          
          {/* Right Section - Actions */}
          <Group gap="xs">
            {/* Multi-Select Mode Toggle */}
            <Tooltip 
              label={multiSelectMode ? "Exit multi-select mode" : "Enter multi-select mode - Click cells to select multiple"} 
              position="bottom" 
              withArrow
            >
              <ActionIcon
                variant={multiSelectMode ? 'filled' : 'light'}
                color={multiSelectMode ? 'blue' : 'gray'}
                size={40}
                onClick={() => {
                  const newMode = !multiSelectMode;
                  setMultiSelectMode(newMode);
                  if (!newMode) {
                    clearSelectedCells(); // Clear selection when exiting mode
                  } else if (localStorage.getItem('multiSelectHintShown') !== 'true') {
                    // Show hint on first use
                    notifications.show({
                      title: 'Multi-Select Mode Active',
                      message: 'Click cells to select multiple. Exit mode to clear selection.',
                      color: 'blue',
                      autoClose: 5000
                    });
                    localStorage.setItem('multiSelectHintShown', 'true');
                  }
                }}
                style={{
                  background: multiSelectMode ? '#1e40af' : '#ffffff',
                  color: multiSelectMode ? 'white' : '#64748b',
                  border: '1px solid #e2e8f0'
                }}
                aria-label="Multi-select mode toggle"
              >
                <IconSelect size={20} />
              </ActionIcon>
            </Tooltip>
            
            {/* Selected cells count badge */}
            {multiSelectMode && selectedCells.length > 0 && (
              <Badge 
                variant="filled" 
                color="blue"
                size="lg"
                style={{ 
                  fontWeight: 600,
                  fontSize: '14px'
                }}
              >
                {selectedCells.length} selected
              </Badge>
            )}
            
            {/* ENHANCED: 09-10-2025 01:20:00 - Legend Info Button */}
            <Tooltip label="View Status Legend" position="bottom" withArrow>
              <ActionIcon
                variant="light"
                color="blue"
                size="lg"
                onClick={() => setShowLegendModal(true)}
                style={{
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                }}
              >
                <IconInfoCircle size={18} />
              </ActionIcon>
            </Tooltip>
            
            <Tooltip label="Toggle advanced filters panel" position="bottom" withArrow>
              <ActionIcon
                variant={showFilters ? 'filled' : 'light'}
                color={showFilters ? 'blue' : 'gray'}
                size={40}
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  background: showFilters ? '#1e40af' : '#ffffff',
                  color: showFilters ? 'white' : '#64748b',
                  border: '1px solid #e2e8f0'
                }}
                aria-label="Toggle filters"
              >
                <IconFilter size={20} />
              </ActionIcon>
            </Tooltip>
            
            <Tooltip label="Copy existing schedule to new dates" position="bottom" withArrow>
              <ActionIcon
                variant="light"
                size={40}
                onClick={handleCopySchedule}
                style={{
                  background: '#ffffff',
                  color: '#64748b',
                  border: '1px solid #e2e8f0'
                }}
                aria-label="Copy schedule"
              >
                <IconCopy size={20} />
              </ActionIcon>
            </Tooltip>
            
            <Tooltip label="Create recurring assignments" position="bottom" withArrow>
              <ActionIcon
                variant="light"
                size={40}
                onClick={handleRecurrenceSchedule}
                style={{
                  background: '#ffffff',
                  color: '#64748b',
                  border: '1px solid #e2e8f0'
                }}
                aria-label="Create recurrence"
              >
                <IconRepeat size={20} />
              </ActionIcon>
            </Tooltip>
            
            <Tooltip label="Publish schedule for doctors to view" position="bottom" withArrow>
              <ActionIcon
                variant="light"
                color="green"
                size={40}
                onClick={handlePublishSchedule}
                style={{
                  background: '#ffffff',
                  color: '#64748b',
                  border: '1px solid #e2e8f0'
                }}
                aria-label="Publish schedule"
              >
                <IconCheck size={20} />
              </ActionIcon>
            </Tooltip>
            
            <Tooltip label="Refresh data and clear cache" position="bottom" withArrow>
              <AnimatedButton
                variant="secondary"
                leftSection={<IconRefresh size={16} />}
                onClick={handleRefresh}
                loading={isLoading}
                size="sm"
              >
                Refresh
              </AnimatedButton>
            </Tooltip>
            
            <Tooltip label="Export schedule to Excel or PDF" position="bottom" withArrow>
              <AnimatedButton
                variant="secondary"
                leftSection={<IconDownload size={16} />}
                onClick={handleExport}
                size="sm"
              >
                Export
              </AnimatedButton>
            </Tooltip>
            
            <Tooltip label="Click to create a new room assignment - Select a room and session time in the grid first" position="bottom" withArrow>
              <AnimatedButton
                variant="primary"
                leftSection={<IconPlus size={16} />}
                onClick={handleCreateAssignment}
                size="sm"
              >
                Create Assignment
              </AnimatedButton>
            </Tooltip>

            <Divider orientation="vertical" />

            <Tooltip label="Manage session time templates" position="bottom" withArrow>
              <Button
                variant="light"
                leftSection={<IconClock size={16} />}
                onClick={() => setShowTemplatesDrawer(true)}
                size="sm"
              >
                Templates
              </Button>
            </Tooltip>
          </Group>
        </Group>
      </Paper>
    );
  };
  
  // =============================================================================
  // RENDER
  // =============================================================================
  
  if (hasError) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Error Loading Planner"
          color="red"
          variant="light"
        >
          <Stack gap="md">
            <Text>{errorMessage}</Text>
            <Button
              leftSection={<IconRefresh size={16} />}
              onClick={handleRefresh}
            >
              Try Again
            </Button>
          </Stack>
        </Alert>
      </Container>
    );
  }
  
  return (
    <Container 
      size="xl" 
      py="md"
      style={{
        ...(isFullscreen && {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          padding: 0,
          margin: 0,
          maxWidth: '100vw',
          height: '100vh'
        })
      }}
    >
      <Stack gap="md" h="100%">
        {/* Enhanced Toolbar - Modern 2025 Styling */}
        {renderToolbar()}
        
        {/* NEW: Active Filters Chips - Removable filter display */}
        {activeFilterChips.length > 0 && (
          <ActiveFiltersChips
            filters={activeFilterChips}
            onRemove={handleRemoveFilterChip}
            onClearAll={clearAllFilters}
            showClearAll={true}
          />
        )}
        
        {/* Filters Panel */}
        {renderFilters()}
        
        {/* Instructions Panel - Modern Accordion Design */}
        {showInstructions && (
          <Accordion variant="contained" radius="md">
            <Accordion.Item value="instructions">
              <Accordion.Control
                icon={
                  <Box
                    style={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                      borderRadius: '8px',
                      padding: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <IconQuestionMark size={16} color="white" />
                  </Box>
                }
              >
                <Text fw={600} c="dark">Quick Start Guide</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="sm">
                  <Group gap="sm" align="flex-start">
                    <Box
                      style={{
                        minWidth: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: '#3b82f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Text size="xs" fw={700} c="white">1</Text>
                    </Box>
                    <Stack gap={4} style={{ flex: 1 }}>
                      <Text size="sm" fw={500}>Click an Empty Cell</Text>
                      <Text size="xs" c="dimmed">
                        Click any green "ASSIGN" cell in the grid below to create a new assignment
                      </Text>
                    </Stack>
                  </Group>
                  
                  <Group gap="sm" align="flex-start">
                    <Box
                      style={{
                        minWidth: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: '#3b82f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Text size="xs" fw={700} c="white">2</Text>
                    </Box>
                    <Stack gap={4} style={{ flex: 1 }}>
                      <Text size="sm" fw={500}>Use Quick Actions</Text>
                      <Text size="xs" c="dimmed">
                        Click the "Create Assignment" button in the toolbar for advanced options
                      </Text>
                    </Stack>
                  </Group>
                  
                  <Group gap="sm" align="flex-start">
                    <Box
                      style={{
                        minWidth: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: '#3b82f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Text size="xs" fw={700} c="white">3</Text>
                    </Box>
                    <Stack gap={4} style={{ flex: 1 }}>
                      <Text size="sm" fw={500}>Select Doctor & Finalize</Text>
                      <Text size="xs" c="dimmed">
                        Choose a doctor from the dropdown, add optional notes, and click "Create" to save
                      </Text>
                    </Stack>
                  </Group>
                  
                  <Divider my="xs" />
                  
                  <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
                    <Text size="xs">
                      <strong>Tip:</strong> Use Ctrl+Click (Cmd+Click on Mac) to select multiple cells for bulk assignment
                    </Text>
                  </Alert>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        )}

        {/* Main Content Area */}
        <Box style={{ flex: 1, minHeight: 0 }}>
          {isLoading ? (
            <Center h="400px">
              <Stack align="center" gap="md">
                <Box
                  style={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                    borderRadius: '50%',
                    padding: '20px',
                    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
                  }}
                >
                  <Loader size="xl" color="white" />
                </Box>
                <Text c="dimmed" size="lg" fw={500}>
                  Loading hospital data...
                </Text>
                <Text c="dimmed" size="sm">
                  Please wait while we fetch the latest schedule information
                </Text>
              </Stack>
            </Center>
          ) : (
            <>
              {activeView === 'grid' ? (
                <Box style={{ position: 'relative' }}>
                  <GridErrorBoundary>
                    <RoomCentricPlannerGrid
                      data={plannerData?.data || null}
                      loading={gridLoading}
                      error={(gridError as any)?.message || undefined}
                      dateRange={dateRange}
                      onRefresh={handleRefresh}
                      onExport={handleExport}
                      onRoomClick={(room) => {
                        // Open room drawer when room card is clicked
                        setSearchParams({ roomId: room.id });
                      }}
                      onSessionClick={(room, sessionTemplateId, date) => {
                        // Handle session click for assignment
                        const sessionTime = room.sessionTimes.find(st => st.id === sessionTemplateId);
                        if (sessionTime) {
                          handleAssignmentData({
                            roomId: room.id,
                            sessionTemplateId,
                            date: date.toISOString().split('T')[0]
                          });
                        }
                      }}
                      onRoomAction={(action, room) => {
                        switch (action) {
                          case 'edit':
                            // Open room drawer for editing
                            setSearchParams({ roomId: room.id });
                            break;
                          case 'delete':
                            // Show room deletion confirmation modal
                            setRoomToDelete(room);
                            setShowDeleteRoomModal(true);
                            break;
                          case 'configure':
                            // Open room drawer to Sessions tab for configuration
                            setSearchParams({ roomId: room.id });
                            break;
                        }
                      }}
                      onSessionAssign={(room, sessionTemplateId, date) => {
                        handleAssignmentData({
                          roomId: room.id,
                          sessionTemplateId,
                          date: date.toISOString().split('T')[0]
                        });
                      }}
                      holidays={holidaysData?.data?.holidays as any || []} // FIXED: 08-10-2025 20:21:00 - Type cast for holiday compatibility
                    />
                  </GridErrorBoundary>
                  
                  {/* Bulk Action Toolbar (NEW: 08-10-2025 - Multi-select CTA) */}
                  <BulkActionToolbar
                    selectedCount={selectedCells.length}
                    onAssign={() => setShowBulkAssignModal(true)}
                    onClear={() => clearSelectedCells()}
                  />
                  
                  {/* Legend overlay for Grid view */}
                  <Box
                    style={{
                      position: 'fixed',
                      bottom: '24px',
                      right: '24px',
                      zIndex: 50,
                    }}
                  >
                    <FloorPlanLegend
                      items={[
                        { id: 'available', label: 'Available', color: '#e8f5e8', count: 0 },
                        { id: 'occupied', label: 'Occupied', color: '#ffeaa7', count: 0 },
                        { id: 'blocked', label: 'Blocked', color: '#fab1a0', count: 0 },
                        { id: 'inactive', label: 'Inactive', color: '#ddd', count: 0 },
                        { id: 'conflict', label: 'Conflict', color: '#fa5252', count: 0 },
                      ]}
                      position="bottom-right"
                      compact={true}
                      alwaysVisible={true}
                      showCounts={false}
                    />
                  </Box>
                </Box>
              ) : activeView === 'calendar' ? (
                // ADDED: 10-10-2025 21:02:31 - Calendar View Integration
                // FIXED: 10-10-2025 22:50:00 - Added stable key and memoized initialDate to prevent re-mounting
                <Box>
                  <RoomCalendarView
                    key="calendar-view-stable"
                    rooms={plannerData?.data?.rooms || []}
                    sessionTemplates={plannerData?.data?.rooms?.flatMap((r: any) => r.sessionTimes || []) || []}
                    cellStatuses={plannerData?.data?.cellStatuses || []}
                    assignments={plannerData?.data?.assignments || []}
                    isLoading={gridLoading}
                    initialViewMode={viewMode as 'day' | 'week' | 'month'}
                    initialDate={stableInitialDate}
                    onCellClick={(roomId, sessionId, date) => {
                      // Handle cell click for assignment creation
                      const room = plannerData?.data?.rooms.find((r: any) => r.id === roomId);
                      const sessionTime = room?.sessionTimes?.find((st: any) => st.id === sessionId);
                      if (room && sessionTime) {
                        handleAssignmentData({
                          roomId,
                          sessionTemplateId: sessionId,
                          date: date.toISOString().split('T')[0]
                        });
                      }
                    }}
                    onAssignmentClick={(assignment) => {
                      // Handle assignment click to open details drawer
                      setSearchParams({ assignmentId: assignment.id });
                    }}
                    onDateRangeChange={handleCalendarDateRangeChange}
                  />
                </Box>
              ) : (
                <FloorPlanErrorBoundary>
                  <FloorPlanCanvas
                    data={floorPlanData?.data || null}
                    selectedDate={dateRange[0]} // Use start of date range for floor plan
                    onRoomClick={handleRoomClick}
                    onRoomDoubleClick={handleRoomDoubleClick} // ADDED: 09-10-2025 12:12:58 - Opens assign modal
                    onAssignmentClick={handleAssignmentClick}
                    onContextMenuAction={handleContextMenuAction} // ADDED: 09-10-2025 12:12:58 - Right-click menu actions
                    loading={floorPlanLoading}
                    error={(floorPlanError as any)?.message || undefined}
                    showToolbar={true}
                    showLegend={true}
                    enableGridSnap={true}
                    enableFullscreen={true}
                    enableRealTimeSync={true}
                    legendPosition="bottom-right"
                    legendAlwaysVisible={true}
                    activeLegendFilters={legendFilters} // ADDED: 09-10-2025 12:12:58 - Legend filtering state
                    onLegendFilterChange={toggleLegendFilter} // ADDED: 09-10-2025 12:12:58 - Toggle filter on click
                    onClearLegendFilters={clearLegendFilters} // ADDED: 09-10-2025 12:12:58 - Clear all filters
                  />
                </FloorPlanErrorBoundary>
              )}
            </>
          )}
        </Box>
      </Stack>
      
      {/* Side Panel */}
      {renderSidePanel()}
      
      {/* Modals */}
      <AssignModal
        opened={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setAssignmentData(null);
        }}
        {...(assignmentData && {
          room: {
            id: assignmentData.roomId,
            number: plannerData?.data?.rooms.find((r: any) => r.id === assignmentData.roomId)?.number || '',
            name: plannerData?.data?.rooms.find((r: any) => r.id === assignmentData.roomId)?.name || '',
            capacity: plannerData?.data?.rooms.find((r: any) => r.id === assignmentData.roomId)?.capacity || 0,
            isActive: true
          }
        })}
        {...(assignmentData && assignmentData.sessionTemplateId && {
          sessionTime: {
            id: assignmentData.sessionTemplateId,
            name: plannerData?.data?.rooms
              .flatMap((r: any) => r.sessionTimes)
              .find((st: any) => st.id === assignmentData.sessionTemplateId)?.name || '',
            startTime: plannerData?.data?.rooms
              .flatMap((r: any) => r.sessionTimes)
              .find((st: any) => st.id === assignmentData.sessionTemplateId)?.startTime || '',
            endTime: plannerData?.data?.rooms
              .flatMap((r: any) => r.sessionTimes)
              .find((st: any) => st.id === assignmentData.sessionTemplateId)?.endTime || ''
          }
        })}
        date={assignmentData ? new Date(assignmentData.date) : dateRange[0]}
        doctors={filtersData?.data?.users || []}
        // ENHANCED: 08-10-2025 22:30:00 - Pass filter options for hierarchical selection
        filterOptions={filterOptions}
        contextData={{
          selectedBuilding,
          selectedFloor,
          selectedRooms,
          dateRange
        }}
      />
      
      <BulkAssignModal
        opened={showBulkAssignModal}
        onClose={() => setShowBulkAssignModal(false)}
        onConfirm={async ({ doctorId, onlyVacancies }) => {
          // TODO: Implement bulk assignment
          console.log('Bulk assign:', { doctorId, onlyVacancies });
          setShowBulkAssignModal(false);
          return [];
        }}
        items={[]}
        doctors={filtersData?.data?.users || []}
      />
      
      <ExportModal
        opened={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={async ({ format, scope, filename }: any) => {
          try {
            // Build query parameters for the export API
            const queryParams = new URLSearchParams({
              format,
              startDate: scope.dateRange.start.toISOString().split('T')[0],
              endDate: scope.dateRange.end.toISOString().split('T')[0],
              includeAssignments: scope.includeAssignments.toString(),
              includeBlocks: scope.includeBlocks.toString(),
              includeConflicts: scope.includeConflicts.toString(),
              includeUtilization: scope.includeUtilization.toString()
            });

            // Add building/floor/room filters if specified
            if (scope.buildings && scope.buildings.length > 0) {
              queryParams.append('buildingIds', scope.buildings.join(','));
            }
            if (scope.floors && scope.floors.length > 0) {
              queryParams.append('floorIds', scope.floors.join(','));
            }
            if (scope.rooms && scope.rooms.length > 0) {
              queryParams.append('roomIds', scope.rooms.join(','));
            }
            if (scope.sessionTimes && scope.sessionTimes.length > 0) {
              queryParams.append('sessionTemplateIds', scope.sessionTimes.join(','));
            }

            const response = await fetch(`${API_CONFIG.BASE_URL}/planner/export?${queryParams}`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                'Content-Type': 'application/json'
              }
            });

            if (!response.ok) {
              throw new Error(`Export failed: ${response.status} ${response.statusText}`);
            }

            const result = await response.json();
            
            if (!result.success) {
              throw new Error(result.error?.message || 'Export failed');
            }

            // Create and download the file
            const blob = new Blob([JSON.stringify(result.data, null, 2)], { 
              type: format === 'json' ? 'application/json' : 
                    format === 'csv' ? 'text/csv' : 
                    format === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' :
                    'application/pdf'
            });
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return { 
              success: true, 
              filename, 
              downloadUrl: url 
            };
          } catch (error) {
            console.error('Export error:', error);
            throw error;
          }
        }}
        defaultScope={{
          dateRange: {
            start: dateRange[0],
            end: dateRange[1]
          }
        }}
      />
      
      {/* Copy Modal */}
      <CopyModal
        opened={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        sourceData={{
          date: format(dateRange[0], 'yyyy-MM-dd'),
          roomIds: selectedRooms
        }}
      />
      
      {/* Recurrence Modal */}
      <RecurrenceModal
        opened={showRecurrenceModal}
        onClose={() => setShowRecurrenceModal(false)}
        plannerContext={{
          selectedRooms,
          selectedUsers,
          selectedSessionTimes,
          selectedBuilding,
          selectedFloor
        }}
      />
      
      {/* Publish Modal */}
      <PublishModal
        opened={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        publishData={{
          date: format(dateRange[0], 'yyyy-MM-dd'),
          scope: viewMode, // Use current view mode (day/week/month)
          buildingId: selectedBuilding || '',
          floorId: selectedFloor || ''
        }}
      />
      
      {/* Room Detail Drawer */}
      <RoomDetailSidebar
        roomId={roomId}
        opened={!!roomId}
        onClose={() => setSearchParams({})}
      />
      
      {/* Room Deletion Confirmation Modal */}
      <Modal
        opened={showDeleteRoomModal}
        onClose={() => {
          setShowDeleteRoomModal(false);
          setRoomToDelete(null);
        }}
        title="Delete Room"
        size="md"
        centered
      >
        {roomToDelete && (
          <Stack gap="md">
            <Alert
              icon={<IconAlertTriangle size={16} />}
              title="This action cannot be undone"
              color="red"
              variant="light"
            >
              This will permanently delete the room and all associated data including:
              <ul style={{ marginTop: 8, marginLeft: 16 }}>
                <li>Room assignments</li>
                <li>Session times</li>
                <li>Room blocks</li>
                <li>All scheduling history</li>
              </ul>
            </Alert>
            
            <Card shadow="sm" padding="md" radius="md" withBorder>
              <Stack gap="sm">
                <Text fw={600} size="sm" c="dimmed">ROOM TO BE DELETED</Text>
                <Group gap="sm">
                  <IconDoor size={18} color="var(--mantine-color-red-6)" />
                  <Text fw={600} size="lg">
                    {roomToDelete.number} - {roomToDelete.name}
                  </Text>
                </Group>
                <Group gap="xs">
                  <IconBuilding size={14} color="var(--mantine-color-gray-6)" />
                  <Text size="sm" c="dimmed">
                    {roomToDelete.floor.building.name}
                  </Text>
                  <IconStairs size={14} color="var(--mantine-color-gray-6)" />
                  <Text size="sm" c="dimmed">
                    {roomToDelete.floor.name}
                  </Text>
                </Group>
                <Group gap="xs">
                  <Badge color="red" variant="light">
                    {roomToDelete.capacity} Capacity
                  </Badge>
                  <Badge
                    color={roomToDelete.isActive ? 'green' : 'red'}
                    variant="light"
                  >
                    {roomToDelete.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </Group>
              </Stack>
            </Card>
            
            <TextInput
              label="Type 'delete-room' to confirm deletion"
              placeholder="delete-room"
              required
              onChange={(e) => {
                const value = e.currentTarget.value;
                if (value === 'delete-room') {
                  // Enable delete button
                }
              }}
            />
            
            <Group justify="flex-end" gap="sm">
              <Button
                variant="light"
                onClick={() => {
                  setShowDeleteRoomModal(false);
                  setRoomToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={() => {
                  // TODO: Implement actual room deletion
                  notifications.show({
                    title: 'Room Deleted',
                    message: `Room ${roomToDelete.number} has been deleted`,
                    color: 'red'
                  });
                  setShowDeleteRoomModal(false);
                  setRoomToDelete(null);
                }}
              >
                Delete Room
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* ENHANCED: 09-10-2025 01:25:00 - Professional Status Legend Modal */}
      <StatusLegendModal 
        opened={showLegendModal} 
        onClose={() => setShowLegendModal(false)} 
      />

      {/* NEW: 10-10-2025 19:49:52 - Session Templates Management Drawer */}
      <SessionTemplatesDrawer
        opened={showTemplatesDrawer}
        onClose={() => setShowTemplatesDrawer(false)}
      />
    </Container>
  );
};

export default EnhancedPlannerPage;
